import { z } from 'zod';
import {
    allowedFirstSegmentDays,
    isFractionedModality,
    resolveFirstSegmentDays,
    resolveSecondSegmentDays,
} from '../utils/vacationFractioning';
import { computeEndDate } from '../utils/vacationRules';
import { buildHolidayBlockMessage, findBlockingHoliday } from '../utils/vacationHolidayRules';
import {
    BLOCKED_WEEKDAY_MESSAGE,
    MIN_ADVANCE_MESSAGE,
    buildEarliestStartMessage,
    buildScheduleLimitMessage,
    isAfterScheduleLimit,
    isBeforeEarliestStartDate,
    isBeforeMinAdvanceDate,
    isBlockedStartWeekday,
} from '../utils/vacationStartDateRules';
import type { VacationModality } from './vacation.types';

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const MAX_CLIENT_EMAILS = 5;

const clientEmailsField = z
    .array(z.object({ value: z.union([z.literal(''), z.string().email('E-mail do cliente inválido')]) }))
    .max(MAX_CLIENT_EMAILS, `Você pode adicionar no máximo ${MAX_CLIENT_EMAILS} e-mails de cliente`)
    .refine(
        (entries) => {
            const filled = entries.map((entry) => entry.value).filter((value) => value !== '');
            return new Set(filled).size === filled.length;
        },
        { message: 'Os e-mails de cliente não podem se repetir' }
    );

const isoDateField = (requiredMessage: string) =>
    z
        .string({ required_error: requiredMessage })
        .min(1, requiredMessage)
        .regex(ISO_DATE_REGEX, 'Data inválida');

const optionalIsoDateField = z.union([z.literal(''), z.string().regex(ISO_DATE_REGEX, 'Data inválida')]);

const modalityField = z
    .union([
        z.enum(['30', '10+20', '15+15'] as const),
        z.literal(''),
    ])
    .refine((value): value is VacationModality => value !== '', {
        message: 'Selecione a modalidade de férias',
    });

export interface VacationRequestFormSchemaOptions {
    earliestStartDate?: string | null;
    scheduleLimitDate?: string | null;
    holidays?: string[];
    today?: Date;
}

export const createVacationRequestFormSchema = ({
    earliestStartDate,
    scheduleLimitDate,
    holidays = [],
    today,
}: VacationRequestFormSchemaOptions = {}) =>
    z
        .object({
            modality: modalityField,
            firstStartDate: isoDateField('Informe a data de início das férias'),
            firstEndDate: optionalIsoDateField,
            secondStartDate: optionalIsoDateField,
            sellDaysRequested: z.boolean(),
            managerEmail: z
                .string()
                .min(1, 'Informe o e-mail do gestor')
                .email('E-mail do gestor inválido')
                .refine((email) => email.endsWith('@rarolabs.com.br'), {
                    message: 'E-mail do gestor deve ser do domínio @rarolabs.com.br',
                }),
            clientEmails: clientEmailsField,
        })
        .superRefine((data, ctx) => {
            const modality = data.modality as VacationModality | '';
            if (!modality) return;

            if (isBlockedStartWeekday(data.firstStartDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['firstStartDate'],
                    message: BLOCKED_WEEKDAY_MESSAGE,
                });
            }

            if (isBeforeMinAdvanceDate(data.firstStartDate, today ?? new Date())) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['firstStartDate'],
                    message: MIN_ADVANCE_MESSAGE,
                });
            }

            if (isBeforeEarliestStartDate(data.firstStartDate, earliestStartDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['firstStartDate'],
                    message: buildEarliestStartMessage(earliestStartDate as string),
                });
            }

            if (isAfterScheduleLimit(data.firstStartDate, scheduleLimitDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['firstStartDate'],
                    message: buildScheduleLimitMessage(scheduleLimitDate as string),
                });
            }

            const firstHoliday = findBlockingHoliday(data.firstStartDate, holidays);
            if (firstHoliday) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['firstStartDate'],
                    message: buildHolidayBlockMessage(data.firstStartDate, firstHoliday),
                });
            }

            if (!isFractionedModality(modality)) {
                return;
            }

            if (data.sellDaysRequested) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['sellDaysRequested'],
                    message: 'A venda de dias só é possível na modalidade de 30 dias corridos.',
                });
            }

            if (!data.firstEndDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['firstEndDate'],
                    message: 'Informe a data de fim do 1º período',
                });
                return;
            }

            const firstDays = resolveFirstSegmentDays(modality, data.firstStartDate, data.firstEndDate);
            if (firstDays === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['firstEndDate'],
                    message: `O 1º período precisa ter ${allowedFirstSegmentDays(modality).join(' ou ')} dias.`,
                });
                return;
            }

            if (!data.secondStartDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['secondStartDate'],
                    message: 'Informe a data de início do 2º período',
                });
                return;
            }

            if (data.secondStartDate <= data.firstEndDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['secondStartDate'],
                    message: 'O 2º período deve começar depois do fim do 1º',
                });
                return;
            }

            if (isBlockedStartWeekday(data.secondStartDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['secondStartDate'],
                    message: BLOCKED_WEEKDAY_MESSAGE,
                });
            }

            if (isBeforeMinAdvanceDate(data.secondStartDate, today ?? new Date())) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['secondStartDate'],
                    message: MIN_ADVANCE_MESSAGE,
                });
            }

            if (isBeforeEarliestStartDate(data.secondStartDate, earliestStartDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['secondStartDate'],
                    message: buildEarliestStartMessage(earliestStartDate as string),
                });
            }

            if (isAfterScheduleLimit(data.secondStartDate, scheduleLimitDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['secondStartDate'],
                    message: buildScheduleLimitMessage(scheduleLimitDate as string),
                });
            }

            const secondHoliday = findBlockingHoliday(data.secondStartDate, holidays);
            if (secondHoliday) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['secondStartDate'],
                    message: buildHolidayBlockMessage(data.secondStartDate, secondHoliday),
                });
            }
        });

export const VacationRequestFormSchema = createVacationRequestFormSchema();

export type VacationRequestFormValues = z.input<typeof VacationRequestFormSchema>;

export type VacationRequestFormData = z.output<typeof VacationRequestFormSchema>;

export const vacationRequestFormDefaults: VacationRequestFormValues = {
    modality: '',
    firstStartDate: '',
    firstEndDate: '',
    secondStartDate: '',
    sellDaysRequested: false,
    managerEmail: '',
    clientEmails: [],
};

export const createVacationDecisionSchema = (kind: 'approve' | 'reject') =>
    z.object({
        note:
            kind === 'reject'
                ? z
                      .string({ required_error: 'Informe o motivo da reprovação' })
                      .trim()
                      .min(10, 'Descreva o motivo com pelo menos 10 caracteres')
                : z.string().trim().optional().or(z.literal('')),
    });

export type VacationDecisionFormData = {
    note?: string;
};

/**
 * Um formato só, seja a solicitação de um período (30 dias) ou dois (10+20, 15+15): cada período
 * decidido entra em `segmentDecisions` pela própria ordem ('1', '2', ...).
 * Valida apenas os períodos não aprovados (pendentes).
 */
export const createDpDecisionSchema = ({ hasSellRequest = false, segmentCount = 1, pendingOrders }: { hasSellRequest?: boolean; segmentCount?: number; pendingOrders?: string[] } = {}) => {
    const ordersToValidate = (pendingOrders && pendingOrders.length > 0) ? pendingOrders : Array.from({ length: segmentCount }, (_, i) => String(i + 1));

    return z
        .object({
            segmentDecisions: z
                .record(
                    z.string(),
                    z.object({
                        decision: z.enum(['approve', 'reject']).optional(),
                        note: z.string().trim(),
                    })
                )
                .refine(
                    (decisions) => ordersToValidate.every((order) => Boolean(decisions[order]?.decision)),
                    'Decida todos os períodos antes de confirmar'
                ),
            sellDecision: z.enum(['approve', 'reject']).optional(),
        })
        .superRefine((data, ctx) => {
            Object.entries(data.segmentDecisions).forEach(([order, seg]) => {
                if (seg.decision === 'reject' && seg.note.trim().length < 10) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['segmentDecisions', order, 'note'],
                        message: 'Descreva o motivo com pelo menos 10 caracteres',
                    });
                }
            });

            const hasApproved = Object.values(data.segmentDecisions).some((d) => d.decision === 'approve');
            if (hasSellRequest && hasApproved && !data.sellDecision) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['sellDecision'],
                    message: 'Escolha se vai aprovar ou reprovar o abono',
                });
            }
        });
};

export type SegmentDecisionInput = {
    decision: 'approve' | 'reject';
    note: string;
};

export type DpDecisionFormData = {
    segmentDecisions: Record<string, SegmentDecisionInput>;
    sellDecision?: 'approve' | 'reject';
};
